import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { normalizePhone } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Truck, Loader2, CalendarClock, Warehouse, MapPin } from "lucide-react";

const SERVICE_TYPES = [
  ["FEDEX_FREIGHT_PRIORITY", "FedEx Freight Priority"],
  ["FEDEX_FREIGHT_ECONOMY", "FedEx Freight Economy"],
];
const PACKAGE_LOCATIONS = [
  ["LOADING_DOCK", "Loading dock"],
  ["FRONT", "Front"],
  ["REAR", "Rear"],
  ["SIDE", "Side"],
  ["RECEIVING", "Receiving"],
];

function Field({ label, children, required }) {
  return (
    <div>
      <Label className="label-caps text-[10px]">{label}{required && <span className="text-[hsl(var(--destructive))]"> *</span>}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default function FedExBookDialog({ open, onOpenChange, quote, onBooked }) {
  const [form, setForm] = useState({});
  const [origins, setOrigins] = useState([]);
  const [terminals, setTerminals] = useState(null);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    if (!(open && quote)) return;
    let cancelled = false;
    (async () => {
      let list = [];
      try {
        const { data } = await api.get("/ship-origins");
        list = Array.isArray(data) ? data.filter((o) => o.fedex_account_number) : [];
      } catch { /* non-fatal */ }
      if (cancelled) return;
      setOrigins(list);
      const oz = String(quote.origin_zip || "").slice(0, 5);
      const pick = list.find((o) => String(o.zip || "").slice(0, 5) === oz)
        || list.find((o) => o.is_default) || list[0] || null;
      const sel = (quote.rates || []).find((r) => r.carrier_code === quote.selected_carrier_code);
      setForm({
        ship_origin_id: pick?.id || "",
        shipper_company: pick?.company || quote.origin_company || "Premier Handling Solutions",
        shipper_contact: quote.origin_contact_name || "",
        shipper_phone: quote.origin_contact_phone || "",
        shipper_street: pick?.address1 || quote.origin_address1 || "",
        shipper_city: pick?.city || quote.origin_city || "",
        shipper_state: pick?.state || quote.origin_state || "",
        shipper_zip: pick?.zip || quote.origin_zip || "",
        recipient_company: quote.dest_company || quote.customer_name || "",
        recipient_contact: quote.dest_contact_name || "",
        recipient_phone: quote.dest_contact_phone || "",
        recipient_street: quote.dest_address1 || "",
        recipient_residential: false,
        service_type: (sel?.service_type && sel.service_type.startsWith("FEDEX_") ? sel.service_type : null)
          || quote.fedex_service_type || "FEDEX_FREIGHT_PRIORITY",
        schedule_pickup: true,
        pickup_date: quote.pickup_date ? String(quote.pickup_date).slice(0, 10) : "",
        ready_time: "12:00",
        close_time: "15:00",
        package_location: "LOADING_DOCK",
        delivery_location: "LOADING_DOCK",
        notify_email: quote.dest_contact_email || quote.customer_email || "",
        bol_number: quote.quote_number || "",
        po_number: quote.quote_ref || "",
        special_instructions: "",
        bill_to_third_party: false,
        billto_account: "",
        billto_company: "Premier Handling Solutions",
        billto_phone: "",
        billto_street: "1415 Davis Rd",
        billto_city: "Elgin",
        billto_state: "IL",
        billto_zip: "60123",
      });
      try {
        const { data } = await api.get(`/quotes/${quote.id}/terminals`);
        if (!cancelled) setTerminals(data);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [open, quote]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickOrigin = (id) => {
    const o = origins.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      ship_origin_id: id,
      shipper_company: o?.company || f.shipper_company,
      shipper_street: o?.address1 || "",
      shipper_city: o?.city || "",
      shipper_state: o?.state || "",
      shipper_zip: o?.zip || "",
    }));
  };

  const shipperPhoneBad = !!form.shipper_phone?.trim() && !normalizePhone(form.shipper_phone);
  const recipientPhoneBad = !!form.recipient_phone?.trim() && !normalizePhone(form.recipient_phone);

  const book = async () => {
    if (!form.recipient_street?.trim())
      return toast.error("Consignee street address is required by FedEx Freight.");
    if (form.schedule_pickup && !form.pickup_date)
      return toast.error("Pickup date is required to schedule the FedEx pickup.");
    if (shipperPhoneBad || recipientPhoneBad)
      return toast.error("Enter valid 10-digit US phone numbers (or leave them blank).");
    setBooking(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/fedex-book`, form);
      toast.success(`Booked via FedEx Freight — PRO ${data.pro_number}.`, {
        description: data.pickup_confirmation
          ? `Pickup scheduled — confirmation ${data.pickup_confirmation}.`
          : data.pickup_error
            ? `BOL generated, but pickup was NOT scheduled: ${data.pickup_error}`
            : data.has_bol ? "BOL generated." : "Booked, but no BOL document was returned.",
      });
      onOpenChange(false);
      onBooked?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBooking(false);
    }
  };

  if (!quote) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="fedex-book-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Truck className="h-5 w-5 text-[#4D148C]" /> Book via FedEx Freight — {quote.quote_number}
          </DialogTitle>
          <DialogDescription>
            Creates a real FedEx Freight LTL shipment on PHS&apos;s account and generates the Bill of Lading.
            FedEx account rates apply only to lanes shipping from/to a PHS ship-from location.
          </DialogDescription>
        </DialogHeader>

        {quote.fedex_pro_number && (
          <div className="rounded-md border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 px-3 py-2 text-sm" data-testid="fedex-existing-pro">
            Already booked via FedEx — PRO <span className="font-bold">{quote.fedex_pro_number}</span>.
          </div>
        )}

        <div className="space-y-4">
          <Field label="Service Type">
            <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
              <SelectTrigger className="h-9" data-testid="fedex-service-type"><SelectValue /></SelectTrigger>
              <SelectContent>{SERVICE_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>

          <div className="rounded-md border border-[#4D148C]/30 bg-[#4D148C]/5 p-3">
            <div className="label-caps mb-2 flex items-center gap-1.5 text-[#4D148C]">
              <CalendarClock className="h-3.5 w-3.5" /> FedEx Pickup — a truck is dispatched when you book
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Pickup Date" required>
                <Input type="date" data-testid="fedex-pickup-date" value={form.pickup_date || ""}
                  onChange={(e) => set("pickup_date", e.target.value)} className="h-9" />
              </Field>
              <Field label="Ready Time">
                <Input type="time" data-testid="fedex-ready-time" value={form.ready_time || ""}
                  onChange={(e) => set("ready_time", e.target.value)} className="h-9" />
              </Field>
              <Field label="Dock Close">
                <Input type="time" data-testid="fedex-close-time" value={form.close_time || ""}
                  onChange={(e) => set("close_time", e.target.value)} className="h-9" />
              </Field>
            </div>
            <div className="mt-2">
              <Field label="Pickup Notification Email (optional)">
                <Input data-testid="fedex-notify-email" value={form.notify_email || ""}
                  onChange={(e) => set("notify_email", e.target.value)} className="h-9" placeholder="dock@customer.com" />
              </Field>
            </div>
          </div>

          {terminals && (
            <div className="rounded-lg border border-border bg-muted/40 p-3" data-testid="fedex-terminals">
              <div className="label-caps mb-1.5 text-muted-foreground">LTL Servicing Terminals</div>
              {(terminals.origin || terminals.destination) ? (
                <div className="grid grid-cols-2 gap-3 text-xs">
                  {[["Origin", terminals.origin], ["Destination", terminals.destination]].map(([lbl, t]) => (
                    <div key={lbl}>
                      <div className="font-semibold text-foreground">{lbl}</div>
                      {t ? (
                        <div className="text-muted-foreground">
                          {t.name || t.city}{t.code ? ` · ${t.code}` : ""}
                          {t.city && <div>{[t.city, t.state, t.zip].filter(Boolean).join(", ")}</div>}
                          {t.phone && <div>P: {t.phone}</div>}
                        </div>
                      ) : <div className="text-muted-foreground">—</div>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground">{terminals.note || "Not available."}</p>
              )}
            </div>
          )}

          <div className="rounded-lg border border-[#4D148C]/40 bg-[#4D148C]/5 p-3">
            <div className="label-caps mb-2 flex items-center gap-1.5 text-[#4D148C]">
              <Warehouse className="h-3.5 w-3.5" /> Ship From / Pickup Location (Origin · {quote.origin_zip})
            </div>
            <Field label="PHS FedEx Account / Location">
              <Select value={form.ship_origin_id || ""} onValueChange={pickOrigin}>
                <SelectTrigger className="h-9" data-testid="fedex-ship-origin">
                  <SelectValue placeholder="Select a PHS ship-from location" />
                </SelectTrigger>
                <SelectContent>
                  {origins.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.label} — Acct {o.fedex_account_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Pick a PHS location to prefill, or type the actual pickup address — freight can ship from
              anywhere (1415 Davis, 2801 Alft, Angola, etc.). Billing stays on the selected FedEx
              account&apos;s registered address; only the physical pickup location changes.
            </p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Field label="Company"><Input data-testid="fedex-shipper-company" value={form.shipper_company || ""} onChange={(e) => set("shipper_company", e.target.value)} className="h-9" /></Field>
              <Field label="Street Address"><Input data-testid="fedex-shipper-street" value={form.shipper_street || ""} onChange={(e) => set("shipper_street", e.target.value)} className="h-9" /></Field>
              <Field label="Contact"><Input value={form.shipper_contact || ""} onChange={(e) => set("shipper_contact", e.target.value)} className="h-9" /></Field>
              <Field label="Phone">
                <Input data-testid="fedex-shipper-phone" value={form.shipper_phone || ""} onChange={(e) => set("shipper_phone", e.target.value)}
                  className={`h-9 ${shipperPhoneBad ? "border-[hsl(var(--destructive))] focus-visible:ring-[hsl(var(--destructive))]" : ""}`} />
                {shipperPhoneBad && <p className="mt-1 text-[11px] font-medium text-[hsl(var(--destructive))]" data-testid="fedex-shipper-phone-error">Not a valid 10-digit US phone.</p>}
              </Field>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <Field label="City"><Input data-testid="fedex-shipper-city" value={form.shipper_city || ""} onChange={(e) => set("shipper_city", e.target.value)} className="h-9" /></Field>
              <Field label="State"><Input data-testid="fedex-shipper-state" value={form.shipper_state || ""} onChange={(e) => set("shipper_state", e.target.value.toUpperCase())} className="h-9" maxLength={2} /></Field>
              <Field label="ZIP"><Input data-testid="fedex-shipper-zip" value={form.shipper_zip || ""} onChange={(e) => set("shipper_zip", e.target.value)} className="h-9" /></Field>
            </div>
            <div className="mt-3">
              <Field label="Package Location (pickup)">
                <Select value={form.package_location} onValueChange={(v) => set("package_location", v)}>
                  <SelectTrigger className="h-9" data-testid="fedex-package-location"><SelectValue /></SelectTrigger>
                  <SelectContent>{PACKAGE_LOCATIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          </div>

          <div className="rounded-lg border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/5 p-3">
            <div className="label-caps mb-2 flex items-center gap-1.5 text-[hsl(var(--success))]">
              <MapPin className="h-3.5 w-3.5" /> Ship To (Destination · {quote.destination_zip})
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Company"><Input data-testid="fedex-recipient-company" value={form.recipient_company || ""} onChange={(e) => set("recipient_company", e.target.value)} className="h-9" /></Field>
              <Field label="Street Address" required><Input data-testid="fedex-recipient-street" value={form.recipient_street || ""} onChange={(e) => set("recipient_street", e.target.value)} className="h-9" /></Field>
              <Field label="Contact"><Input value={form.recipient_contact || ""} onChange={(e) => set("recipient_contact", e.target.value)} className="h-9" /></Field>
              <Field label="Phone">
                <Input data-testid="fedex-recipient-phone" value={form.recipient_phone || ""} onChange={(e) => set("recipient_phone", e.target.value)}
                  className={`h-9 ${recipientPhoneBad ? "border-[hsl(var(--destructive))] focus-visible:ring-[hsl(var(--destructive))]" : ""}`} />
                {recipientPhoneBad && <p className="mt-1 text-[11px] font-medium text-[hsl(var(--destructive))]" data-testid="fedex-recipient-phone-error">Not a valid 10-digit US phone.</p>}
              </Field>
            </div>
            <div className="mt-3">
              <Field label="Delivery Location">
                <Select value={form.delivery_location} onValueChange={(v) => set("delivery_location", v)}>
                  <SelectTrigger className="h-9" data-testid="fedex-delivery-location"><SelectValue /></SelectTrigger>
                  <SelectContent>{PACKAGE_LOCATIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <Switch checked={!!form.recipient_residential} onCheckedChange={(v) => set("recipient_residential", v)} data-testid="fedex-residential" />
              Residential delivery
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="BOL Reference #"><Input value={form.bol_number || ""} onChange={(e) => set("bol_number", e.target.value)} className="h-9" data-testid="fedex-bol-number" /></Field>
            <Field label="PO / Shipper ID"><Input value={form.po_number || ""} onChange={(e) => set("po_number", e.target.value)} className="h-9" /></Field>
          </div>

          <Field label="Special Instructions">
            <Textarea rows={2} value={form.special_instructions || ""} onChange={(e) => set("special_instructions", e.target.value)} data-testid="fedex-instructions" />
          </Field>

          {/* Bill-To (defaults to sender pays; toggle to bill 3rd-party / Elgin HQ) */}
          <div className={`rounded-md border p-3 transition-colors ${form.bill_to_third_party ? "border-[#4D148C]/50 bg-[#4D148C]/5" : "border-border"}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">Bill freight charges to a third party</div>
                <div className="text-xs text-muted-foreground">
                  {form.bill_to_third_party
                    ? "Requires a real FedEx Bill-To account # below (PHS's own accounts are Shipper accounts and can't be a third-party payor)."
                    : "Off = the PHS shipper account pays (SENDER) — the standard, working setup."}
                </div>
              </div>
              <Switch checked={!!form.bill_to_third_party} onCheckedChange={(v) => set("bill_to_third_party", v)} data-testid="fedex-billto-toggle" />
            </div>
            {form.bill_to_third_party && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Field label="Bill-To FedEx Account #"><Input value={form.billto_account || ""} onChange={(e) => set("billto_account", e.target.value)} className="h-9" placeholder="defaults to PHS billing account" data-testid="fedex-billto-account" /></Field>
                <Field label="Company"><Input value={form.billto_company || ""} onChange={(e) => set("billto_company", e.target.value)} className="h-9" /></Field>
                <Field label="Street"><Input value={form.billto_street || ""} onChange={(e) => set("billto_street", e.target.value)} className="h-9" /></Field>
                <div className="grid grid-cols-3 gap-2">
                  <Field label="City"><Input value={form.billto_city || ""} onChange={(e) => set("billto_city", e.target.value)} className="h-9" /></Field>
                  <Field label="ST"><Input value={form.billto_state || ""} onChange={(e) => set("billto_state", e.target.value.toUpperCase())} className="h-9" /></Field>
                  <Field label="ZIP"><Input value={form.billto_zip || ""} onChange={(e) => set("billto_zip", e.target.value)} className="h-9" /></Field>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="fedex-book-submit" onClick={book} disabled={booking || shipperPhoneBad || recipientPhoneBad} className="gap-1.5 bg-[#4D148C] text-white hover:bg-[#4D148C]/90">
            {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />} Book Shipment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
